import { useRef, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import ReactCrop, {
  Crop,
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SheetDescription } from "@/components/ui/sheet"

interface Props {
  open: boolean
  setOpen: (value: boolean) => void
  circularCrop?: boolean
  aspect?: number
  originalImage: File | null
  imageSource: string
  handleSave: (file: File | null) => void
}

export default function CropImageModal({
  open,
  setOpen,
  circularCrop,
  aspect,
  originalImage,
  imageSource,
  handleSave,
}: Props) {
  const [isSaving, setIsSaving] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState<Crop>()

  const onImageLoad = (e: any) => {
    if (!aspect) return
    const { naturalWidth, naturalHeight, width, height } = e.currentTarget

    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 100,
        },
        aspect || 1,
        naturalWidth,
        naturalHeight
      ),
      naturalWidth,
      naturalHeight
    )

    const pixelCrop = convertToPixelCrop(crop, width, height)

    setCrop(pixelCrop)
  }

  function getCroppedImg(image: HTMLImageElement, pixelCrop: Crop) {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    const pixelRatio = window.devicePixelRatio || 1
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    canvas.width = pixelCrop.width * pixelRatio * scaleX
    canvas.height = pixelCrop.height * pixelRatio * scaleY

    if (!ctx) return
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    ctx.imageSmoothingQuality = "high"

    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY
    )

    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        resolve(file)
      }, "image/jpeg")
    })
  }

  const cropImage = async () => {
    setIsSaving(true)

    // If we don't have an image source, don't try to crop
    if (!imageSource) {
      setOpen(false)
      setIsSaving(false)
      return
    }

    if (
      !imgRef ||
      !imgRef.current ||
      !crop ||
      crop.height === 0 ||
      crop.width === 0
    ) {
      await handleSave(originalImage)
    } else {
      const croppedImage = (await getCroppedImg(imgRef.current, crop)) as File
      await handleSave(croppedImage)
    }

    setOpen(false)
    setIsSaving(false)
  }

  return (
    <Dialog open={open} onOpenChange={(open) => setOpen(open)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Crop Image</DialogTitle>
          <SheetDescription />
        </DialogHeader>
        {imageSource ? (
          <ReactCrop
            crop={crop}
            circularCrop={circularCrop}
            aspect={aspect}
            locked={aspect !== undefined}
            onChange={(c) => setCrop(c)}
          >
            <img ref={imgRef} src={imageSource} onLoad={onImageLoad} />
          </ReactCrop>
        ) : (
          <div className="flex justify-center items-center h-40 bg-muted rounded-md">
            <p className="text-muted-foreground">No image selected</p>
          </div>
        )}
        <DialogFooter className="flex-row gap-4">
          <Button
            onClick={() => setOpen(false)}
            type="button"
            variant="secondary"
            disabled={!imageSource && isSaving}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            className="flex-1"
            disabled={!imageSource || isSaving}
            type="button"
            onClick={() => cropImage()}
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
